import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';
import global from '../../index.module.scss';
import HeadContainer from '../../head-container';
import { getImageFromS3 } from '../../../imageService';
import { DataLayerEventContext } from '../../../contexts/dataLayerEventContext';
import {
  movieGenreChallengeList,
  movieGenreFinish,
  movieGenreResultList,
  movieGenreStart
} from '../../../consts/movie-genre-challenge';

const OPTION_MARKERS = ['а', 'б', 'в', 'г'];

const MovieGenreChallenge = ({
  setScreenNumber,
  backgroundImage,
  setAfterResultScreen,
  start = movieGenreStart,
  challengeList = movieGenreChallengeList,
  resultList = movieGenreResultList,
  finish = movieGenreFinish
}) => {
  const { sendEvent } = useContext(DataLayerEventContext);
  const [phase, setPhase] = useState('intro');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const currentQuestion = challengeList[step];
  const currentAnswer = answers[step];
  const isFirstStep = step === 0;
  const isLastStep = step === challengeList.length - 1;

  const result = useMemo(() => {
    const items = Object.values(answers);
    if (!items.length) return resultList[0];
    const most = [...items]
      .sort(
        (a, b) =>
          items.filter((v) => v === a).length -
          items.filter((v) => v === b).length
      )
      .pop();
    return resultList[most - 1];
  }, [answers, resultList]);

  const onSelect = useCallback(
    (key) => setAnswers((prev) => ({ ...prev, [step]: key })),
    [step]
  );

  const onNext = useCallback(() => {
    if (!answers[step]) return;
    if (isLastStep) {
      setPhase('result');
      return;
    }
    setStep((prev) => prev + 1);
  }, [answers, step, isLastStep]);

  const onPrev = useCallback(() => {
    if (!isFirstStep) setStep((prev) => prev - 1);
  }, [isFirstStep]);

  const onStart = useCallback(() => {
    sendEvent('start_challenge');
    setPhase('questions');
  }, [sendEvent]);

  const onResultNext = useCallback(() => {
    sendEvent('end_challenge');
    setPhase('finish');
  }, [sendEvent]);

  const onFinishNext = useCallback(() => {
    sendEvent('exit_challenge');
    setAfterResultScreen();
  }, [sendEvent, setAfterResultScreen]);

  if (phase === 'intro')
    return (
      <div
        className={classNames(styles.screen, styles.introScreen)}
        style={{ backgroundImage }}
      >
        <HeadContainer setScreenNumber={setScreenNumber} />
        <div className={styles.introBody}>
          <div className={styles.introHead}>
            <img
              className={styles.introEllipse}
              src={getImageFromS3('images/common/blue-ellipse.png')}
              alt="ellipse"
            />
            <p className={styles.introTitle}>{start.title}</p>
          </div>
          <div className={styles.introBottom}>
            <p className={styles.introDescription}>{start.description}</p>
            <button className={global.menuButton} onClick={onStart}>
              {start.button}
            </button>
          </div>
        </div>
      </div>
    );

  if (phase === 'result')
    return (
      <div className={styles.screen} style={{ backgroundImage }}>
        <HeadContainer setScreenNumber={setScreenNumber} />
        <div className={styles.resultBody}>
          <div className={styles.resultPanel}>
            <img src={result.image} alt={result.title} />
            <p className={styles.resultTitle}>{result.title}</p>
            <p className={styles.resultDescription}>{result.description}</p>
            {result.trailerLink && (
              <a
                className={styles.trailerLink}
                href={result.trailerLink}
                target="_blank"
                rel="noreferrer"
              >
                Трейлери за лінком
              </a>
            )}
          </div>
        </div>
        <button className={global.reviewButton} onClick={onResultNext}>
          Далі
        </button>
      </div>
    );

  if (phase === 'finish')
    return (
      <div className={styles.screen} style={{ backgroundImage }}>
        <HeadContainer setScreenNumber={setScreenNumber} />
        <div className={styles.finishBody}>
          <img className={styles.finishImage} src={finish.image} alt="#" />
          <div className={styles.finishPanel}>
            <p className={styles.finishTitle}>{finish.title}</p>
            <p className={styles.finishDescription}>{finish.description}</p>
          </div>
        </div>
        <button className={global.menuButton} onClick={onFinishNext}>
          Далі
        </button>
      </div>
    );

  return (
    <div className={styles.screen} style={{ backgroundImage }}>
      <HeadContainer setScreenNumber={setScreenNumber} />
      <div className={styles.stepToolbar}>
        {challengeList.map((_, index) => (
          <div
            key={index}
            className={classNames(
              styles.stepItem,
              index === step && styles.activeStepItem
            )}
          />
        ))}
      </div>
      <div className={styles.questionBody}>
        <p className={styles.question}>{currentQuestion.question}</p>
        <div className={styles.optionsList}>
          {currentQuestion.answers.map((answer, index) => (
            <div
              key={answer.key}
              className={classNames(
                styles.optionItem,
                answer.key === currentAnswer && styles.isSelected
              )}
              onClick={() => onSelect(answer.key)}
            >
              <div className={styles.optionMarker}>{OPTION_MARKERS[index]}</div>
              <div className={styles.optionCopy}>{answer.value}</div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.buttonContainer}>
        {!isFirstStep && (
          <button className={global.reviewButton} onClick={onPrev}>
            Назад
          </button>
        )}
        <button className={global.menuButton} onClick={onNext}>
          Далі
        </button>
      </div>
    </div>
  );
};

export default MovieGenreChallenge;
